import { ErrorMessage } from '@hookform/error-message';
import { Business, Warning } from '@mui/icons-material';
import {
  Badge,
  Box,
  Button,
  InputAdornment,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
  getWorkspaceTypeTranslationKey,
  getWorkspaceUserGroup,
  getWorkspaceUserGroupTranslationKey,
} from '~/scenes/MyWorkspace/data';

import { WorkspaceRoleIcon } from '../WorkspaceRoleIcon';

import { useSxStyles } from './styles';
import { useWorkspaceToolbarEditMode } from './useWorkspaceToolbarEditMode';

export const WorkspaceToolbarEditMode = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const {
    isEditing,
    register,
    errors,
    defaultName,
    handleSave,
    handleCancel,
    activeWorkspace,
  } = useWorkspaceToolbarEditMode();

  const typeLabel = t(getWorkspaceTypeTranslationKey(activeWorkspace?.type));
  const userGroupLabel = t(
    getWorkspaceUserGroupTranslationKey(
      getWorkspaceUserGroup(activeWorkspace?.restrictToRoles?.[0]),
    ),
  );

  return (
    <form
      onSubmit={handleSave}
      data-cy="workspace-edit"
      style={{ width: '100%' }}
    >
      <Box sx={sxStyles.grid}>
        <Box sx={sxStyles.nameArea}>
          <Badge
            overlap="circular"
            badgeContent={
              activeWorkspace?.type === 'company' ? (
                <Business fontSize={'inherit'} color={'primary'} />
              ) : undefined
            }
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            sx={{
              marginRight: theme.spacing(1.5),
              '& .MuiBadge-badge': { fontSize: '1rem' },
            }}
            title={`${t('workspaceType')}: ${typeLabel} - ${userGroupLabel}`}
          >
            <WorkspaceRoleIcon type={activeWorkspace?.restrictToRoles?.[0]} />
          </Badge>
          <TextField
            {...register('name')}
            // Provide default value even when form initial state
            // was set to avoid label animation
            defaultValue={defaultName}
            label={t('workspaceName')}
            error={!!errors.name}
            slotProps={{
              inputLabel: {
                required: true,
              },
              input: {
                startAdornment: errors.name && (
                  <InputAdornment position="start">
                    <Tooltip
                      title={<ErrorMessage errors={errors} name="name" />}
                    >
                      <Warning sx={sxStyles.inputAdornmentErrorIcon} />
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            }}
            sx={sxStyles.input}
            variant={'standard'}
            size={'small'}
            data-cy="name"
          />
        </Box>
        <Box sx={sxStyles.emptyArea} />
        <Box sx={sxStyles.submitArea}>
          <Button color="primary" onClick={handleCancel}>
            {t('cancel')}
          </Button>

          <Button
            color="primary"
            variant="contained"
            type="submit"
            data-cy="save"
          >
            {isEditing ? t('apply') : t('save')}
          </Button>
        </Box>
      </Box>
    </form>
  );
};
